import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Statistics {
  total_kpis: number
  active_kpis: number
  meeting_target: number
  below_target: number
  above_target: number
  avg_achievement_pct: number
}

interface KPI {
  id: string
  name: string
  category: string
  current: number
  target: number
  achievement: number
  status: string
}

interface Category {
  category: string
  kpis: number
  avg_achievement: number
}

export default function KPIManagementPage() {
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [kpis, setKpis] = useState<KPI[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, kpisRes, categoriesRes] = await Promise.all([
          fetch('/api/kpimanagement/statistics'),
          fetch('/api/kpimanagement/kpis'),
          fetch('/api/kpimanagement/categories')
        ])
        setStatistics(await statsRes.json())
        setKpis(await kpisRes.json())
        setCategories(await categoriesRes.json())
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      'Above Target': 'bg-green-100 text-green-800',
      'Near Target': 'bg-yellow-100 text-yellow-800',
      'Below Target': 'bg-red-100 text-red-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return <div className="p-8"><Header /><div className="mt-8">Loading KPI Management...</div></div>
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">KPI Management</h1>

        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-blue-600">{statistics.total_kpis}</div>
              <div className="text-sm text-gray-500">Total KPIs</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-green-600">{statistics.meeting_target}</div>
              <div className="text-sm text-gray-500">Meeting Target</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-blue-600">{statistics.above_target}</div>
              <div className="text-sm text-gray-500">Above Target</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-red-600">{statistics.below_target}</div>
              <div className="text-sm text-gray-500">Below Target</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-purple-600">{statistics.active_kpis}</div>
              <div className="text-sm text-gray-500">Active</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-teal-600">{statistics.avg_achievement_pct}%</div>
              <div className="text-sm text-gray-500">Avg Achievement</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Enterprise KPIs</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">KPI</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Current</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Target</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {kpis.map((k) => (
                    <tr key={k.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{k.name}</td>
                      <td className="px-4 py-3 text-gray-600">{k.category}</td>
                      <td className="px-4 py-3 text-gray-600">{k.current.toLocaleString()}</td>
                      <td className="px-4 py-3 text-gray-600">{k.target.toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(k.status)}`}>
                          {k.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Categories</h2></div>
            <div className="p-4 space-y-3">
              {categories.map((c, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{c.category}</span>
                    <span className="text-sm text-gray-500">{c.kpis} KPIs</span>
                  </div>
                  <div className="mt-2">
                    <div className="flex justify-between text-sm text-gray-500 mb-1">
                      <span>Achievement</span>
                      <span>{c.avg_achievement}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full" style={{width: `${c.avg_achievement}%`}}></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
